import { useState, useEffect } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import AlertDialog from 'components/AlertDialog'
import submitOnEnter from 'utils/submitOnEnter'
import scheduleStatusValidator from 'validators/scheduleStatusValidator'
import { ReactComponent as ButtonLoading } from 'icons/loading.svg'
import { Formik } from 'formik'

import {
  Form,
  Row,
  FormField,
  Footer,
  SubTitle,
  InfoText,
  WarningText,
  Input,
  ColorBox,
  Button,
  DangerButton,
  ErrorContainer
} from './styles'

export default function ScheduleStatusModal ({
  visible,
  data: scheduleStatus = null,
  selectedStatus = null,
  onCreate: handleCreate,
  onEdit: handleEdit,
  onDelete: handleDelete,
  onClose: handleClose
}) {
  const [deleting, setDeleting] = useState (false)
  const [errorMessage, setErrorMessage] = useState (null)
  const [showAlertDialog, setShowAlertDialog] = useState (false)
  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  const colorOptions = [
    '#3A3A3A',
    '#6FC3B2',
    '#FFE29A',
    '#B7E3B1',
    '#E2B6E2',
    '#7BC67E',
    '#F3A6A6',
    '#FF6F6F'
  ]

  const toggleAlertDialog = () => setShowAlertDialog (!showAlertDialog)

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='sm' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{scheduleStatus ? 'Editar' : 'Novo'} status personalizado</DialogTitle>
        </DialogHeader>
        <Formik
          validationSchema={scheduleStatusValidator}
          initialValues={scheduleStatus ? {
            'businessId': businessId,
            'name': scheduleStatus.name,
            'codename': scheduleStatus.codename,
            'color': scheduleStatus.color
          } : {
            'businessId': businessId,
            'name': '',
            'codename': '',
            'color': ''
          }}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              if (!!scheduleStatus) {
                const { data } = await cutileiApi.put (
                  `/schedule_statuses/${scheduleStatus.id}`, values, requestConfig
                )
                handleEdit (data)
              } else {
                const { data } = await cutileiApi.post (
                  '/schedule_statuses', values, requestConfig
                )
                handleCreate (data)
              }

              setSubmitting (false)
              handleClose ()
            } catch (error) {
              setErrorMessage (getApiErrorMessage (error))
              setSubmitting (false)
              logApiError (error)
            }
          }}
        >
          {function ScheduleStatusForm ({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            handleChange,
            handleBlur,
            handleSubmit
          }) {
            useEffect (() => {
              generateCodename (values.name)
            }, [values.name])

            const generateCodename = (name = '') => {
              let s = String (name).toLowerCase ()
              if (s.normalize) s = s.normalize ('NFD').replace (/[\u0300-\u036f]/g, '')

              const codename = s.replace (/[^a-z0-9]+/g, '_').replace (/^_+|_+$/g, '')

              setFieldValue ('codename', codename)
            }

            const deleteScheduleStatus = async () => {
              try {
                setDeleting (true)

                await cutileiApi.delete (
                  `/schedule_statuses/${scheduleStatus.id}`, requestConfig
                )

                setDeleting (false)
                handleDelete (scheduleStatus)
                handleClose ()
              } catch (error) {
                setDeleting (false)
                logApiError (error)
                setErrorMessage (getApiErrorMessage (error))
              }
            }

            return (
              <>
                <DialogBody>
                  <Form id='schedule-status-form' onSubmit={handleSubmit} onKeyDown={submitOnEnter(handleSubmit, isSubmitting)}>
                    <Row>
                      <FormField>
                        <Input 
                          placeholder='Nome do status'
                          value={values.name}
                          onChange={handleChange ('name')} 
                          onBlur={handleBlur ('name')}
                        />
                        {touched.name && errors.name && (
                          <WarningText>
                            {errors.name}
                          </WarningText>
                        )}
                      </FormField>
                    </Row>
                    <FormField>
                      <SubTitle>Cor do status</SubTitle>
                      {touched.color && errors.color && (
                        <Row>
                          <WarningText>
                            {errors.color}
                          </WarningText>
                        </Row>
                      )}
                      <Row>
                        {colorOptions.slice (0, 4).map (color => (
                          <ColorBox
                            key={color}
                            color={color}
                            selected={values.color === color}
                            onClick={() => setFieldValue ('color', color)}
                          />
                        ))}
                      </Row>
                      <Row>
                        {colorOptions.slice (4, 8).map (color => (
                          <ColorBox
                            key={color}
                            color={color}
                            selected={values.color === color}
                            onClick={() => setFieldValue ('color', color)}
                          />
                        ))}
                      </Row>
                    </FormField>
                    {errorMessage && (
                      <ErrorContainer>
                        <InfoText>{errorMessage}</InfoText>
                      </ErrorContainer>
                    )}
                  </Form>
                </DialogBody>
                <DialogFooter>
                  <Footer>
                    <Button type='submit' form='schedule-status-form' disabled={isSubmitting}>
                      {isSubmitting ? (
                        <ButtonLoading/>
                      ) : (
                        scheduleStatus ? 'Salvar alterações' : 'Criar status'
                      )}
                    </Button>
                    {selectedStatus !== scheduleStatus?.codename && (
                      <DangerButton onClick={toggleAlertDialog} disabled={deleting}>
                        {deleting ? <ButtonLoading/> : 'Excluir status'}
                      </DangerButton>
                    )}
                    <AlertDialog
                      visible={showAlertDialog}
                      title='Atenção!'
                      message='Deseja realmente excluir este status?'
                      confirmText='Sim'
                      cancelText='Não'
                      onConfirm={deleteScheduleStatus}
                      onClose={toggleAlertDialog}
                      dangerous={true}
                    />
                  </Footer>
                </DialogFooter>
              </>
            )
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}
